/* eslint-disable @typescript-eslint/no-explicit-any */
import { deserialize } from "@flick/flick-api-client";
import { createApi } from "@reduxjs/toolkit/query/react";
import { JwtClient } from "utils/jwtClient";

import { BaseQueryParams } from "./queries.utils";

export type CarbonParams = {
  jwtClient: JwtClient;
  startAt: Date;
  endAt: Date;
  customerNumber: string;
};

type MonthlyUsageResponse = {
  customer?: string;
  flowDirection: string;
  id: string;
  month: number;
  quantity: string;
  totalExclGst: string;
  totalGst: string;
  totalInclGst: string;
  type: string;
  year: number;
};

export type MonthlyUsage = {
  total: MonthlyUsageResponse;
  import: MonthlyUsageResponse;
  export?: MonthlyUsageResponse; // only Solar customers has export usage
};

export type MonthKeys =
  | "01"
  | "02"
  | "03"
  | "04"
  | "05"
  | "06"
  | "07"
  | "08"
  | "09"
  | "10"
  | "11"
  | "12";

/**
 * Define a base query function that calls jwtClient.apiCall
 */
const apiCallBaseQuery = async ({ jwtClient, ...params }: BaseQueryParams) => {
  const response = await jwtClient.apiCall(params);
  const result = await response.json();

  const flatResult: MonthlyUsageResponse[] = deserialize(result);
  return { data: flatResult };
};

/**
 * Define the carbonApi slice
 * this slice uses flick_api to fetch the new carbon measurements data
 * and is using flick-api-client to deserialize the response ( this package only work with flick_api)
 * https://static.flickelectric.co.nz/flick_api/index.html
 */
export const newBillingApi = createApi({
  reducerPath: "newBillingApi",
  baseQuery: apiCallBaseQuery,
  endpoints: (builder) => ({
    getUsagePerMonth: builder.query<
      Partial<Record<MonthKeys, MonthlyUsage>>,
      CarbonParams
    >({
      query({ jwtClient, startAt, endAt, customerNumber }) {
        const queryParams = {
          customer_id: customerNumber,
          start_at: new Date(startAt).toISOString(),
          end_at: new Date(endAt).toISOString(),
        };
        return {
          jwtClient,
          path: "/billing/v1/charge_aggregates",
          queryParams,
        };
      },
      transformResponse: (
        data: MonthlyUsageResponse[],
      ): Partial<Record<MonthKeys, MonthlyUsage>> => {
        const formatData = Array.isArray(data) ? data : [];
        const groupedByMonth = formatData.reduce(
          (acc, item) => {
            const monthKey =
              `${item.month.toString().padStart(2, "0")}` as MonthKeys;

            if (!acc[monthKey]) {
              acc[monthKey] = {
                total: {} as MonthlyUsageResponse,
                import: undefined,
                export: undefined,
              };
            }
            acc[monthKey][item.flowDirection] = item;

            return acc;
          },
          {} as Partial<Record<MonthKeys, MonthlyUsage>>,
        );

        return groupedByMonth;
      },
    }),
  }),
});

export const { useGetUsagePerMonthQuery } = newBillingApi;
